// The panel that opens under the map when a box is clicked. It shows what the
// server said about that part (status and the sentences behind it) and fetches
// the drill-down for it: routes and restarts for the API, restarts for the
// worker, run history for a job.

import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type {
  OpsEdge,
  OpsJobRun,
  OpsJobView,
  OpsMachine,
  OpsOverview,
  OpsProviders,
  OpsRoutesResponse,
  OpsRunRow,
  OpsSeriesPoint,
  OpsStatus,
} from '../../api/ops-types.js';
import {
  CONDITION_LABEL,
  OPERATION_LABEL,
  PROVIDER_LABEL,
  STATUS_LABEL,
  ago,
  dur,
  n0,
  pct,
  perMinute,
  span,
} from './format.js';
import type { NodeId } from './map-model.js';

export const Chip = ({ status, label }: { status: OpsStatus; label?: string }): JSX.Element => (
  <span className={`ops-chip st-${status}`}>{label ?? STATUS_LABEL[status]}</span>
);

function Why({ reasons }: { reasons: readonly string[] }): JSX.Element | null {
  if (reasons.length === 0) return null;
  return (
    <ul className="ops-why">
      {reasons.map((r) => (
        <li key={r}>{r}</li>
      ))}
    </ul>
  );
}

function Tiles({
  items,
}: {
  items: { k: string; v: string; u?: string; st?: OpsStatus }[];
}): JSX.Element {
  return (
    <div className="ops-tiles">
      {items.map((c) => (
        <div key={c.k} className={`ops-tile${c.st && c.st !== 'ok' ? ` st-${c.st}` : ''}`}>
          <div className="k">{c.k}</div>
          <div className={`v${c.v.length > 7 ? ' long' : ''}`}>
            {c.v}
            {c.u ? <small>{c.u}</small> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Spark({
  values,
  label,
}: {
  values: readonly number[];
  label: string;
}): JSX.Element {
  const w = 220;
  const h = 40;
  const max = Math.max(1, ...values);
  const pts = values
    .map(
      (v, i) =>
        `${((i / Math.max(1, values.length - 1)) * w).toFixed(1)},${(h - 2 - (v / max) * (h - 4)).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg
      className="ops-spark"
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      role="img"
      aria-label={label}
    >
      <polyline points={pts} />
    </svg>
  );
}

function Machines({
  machines,
  nowMs,
}: {
  machines: readonly OpsMachine[];
  nowMs: number;
}): JSX.Element {
  if (machines.length === 0) return <p className="muted">No machine has reported yet.</p>;
  return (
    <div className="ops-table-wrap">
      <table className="ops-table">
        <thead>
          <tr>
            <th>Machine</th>
            <th>State</th>
            <th className="num">Up for</th>
            <th className="num">Last heard</th>
            <th className="num">Memory</th>
            <th>Last error</th>
          </tr>
        </thead>
        <tbody>
          {machines.map((m) => {
            const st: OpsStatus =
              m.condition === 'up' ? 'ok' : m.condition === 'stopped' ? 'unknown' : 'bad';
            return (
              <tr key={m.machine} className={st === 'bad' ? 'row-bad' : ''}>
                <td className="mono">{m.machine}</td>
                <td>
                  <Chip status={st} label={CONDITION_LABEL[m.condition]} />
                </td>
                <td className="num">
                  {m.condition === 'up'
                    ? span(nowMs - m.startedAtMs)
                    : `ended ${ago(m.endedAtMs ?? m.lastBeatAtMs, nowMs)}`}
                </td>
                <td className="num">{ago(m.lastBeatAtMs, nowMs)}</td>
                <td className="num">{m.memMb === null ? '—' : `${m.memMb} MB`}</td>
                <td className="err-text">{m.lastError ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Restarts({ service, nowMs }: { service: 'api' | 'worker'; nowMs: number }): JSX.Element {
  const q = useQuery({
    queryKey: ['ops', 'runs', service],
    queryFn: () => apiFetch<{ runs: OpsRunRow[] }>(`/ops/runs?service=${service}&limit=15`),
    refetchInterval: 10_000,
  });
  const runs = q.data?.runs ?? [];
  const interesting = runs.filter((r) => r.condition === 'crashed' || r.condition === 'lost');
  return (
    <section>
      <h3 className="sec">Crashes and restarts</h3>
      {q.isLoading ? <p className="muted">Loading…</p> : null}
      {q.data && interesting.length === 0 ? (
        <p className="muted">No crash or unclean stop on record.</p>
      ) : null}
      {interesting.length > 0 ? (
        <ul className="ops-events">
          {interesting.map((r) => (
            <li key={r.runId}>
              <time>{ago(r.endedAtMs ?? r.lastBeatAtMs, nowMs)}</time>
              <span
                className={`sev ${r.condition === 'crashed' ? 'bad' : 'warn'}`}
                aria-hidden="true"
              />
              <span>
                <b className="mono">{r.machine}</b>{' '}
                {r.condition === 'crashed' ? 'crashed' : 'ended without a clean stop'} after{' '}
                {span((r.endedAtMs ?? r.lastBeatAtMs) - r.startedAtMs)}.
                {r.lastError ? ` Last error: ${r.lastError}` : ' No error was captured.'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

const ROUTE_WINDOWS = [15, 60, 180] as const;

function RouteTable({ nowMs }: { nowMs: number }): JSX.Element {
  const [minutes, setMinutes] = useState<(typeof ROUTE_WINDOWS)[number]>(15);
  const [onlyTrouble, setOnlyTrouble] = useState(false);
  const q = useQuery({
    queryKey: ['ops', 'routes', 'api', minutes],
    queryFn: () => apiFetch<OpsRoutesResponse>(`/ops/services/api/routes?minutes=${minutes}`),
    refetchInterval: 5000,
  });
  const all = q.data?.routes ?? [];
  const rows = onlyTrouble ? all.filter((r) => r.status === 'bad' || r.status === 'warn') : all;
  return (
    <section>
      <h3 className="sec">API routes</h3>
      <div className="ops-filters">
        {ROUTE_WINDOWS.map((m) => (
          <button key={m} type="button" aria-pressed={minutes === m} onClick={() => setMinutes(m)}>
            Last {m === 60 ? 'hour' : m === 180 ? '3 hours' : `${m} min`}
          </button>
        ))}
        <button type="button" aria-pressed={onlyTrouble} onClick={() => setOnlyTrouble((v) => !v)}>
          Only ones needing a look
        </button>
      </div>
      {q.isLoading ? <p className="muted">Loading…</p> : null}
      {q.error ? <p className="error">Could not load the routes.</p> : null}
      {q.data && rows.length === 0 ? (
        <p className="muted">
          {onlyTrouble ? 'No route needs a look in this window.' : 'No calls in this window.'}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Route</th>
                <th className="num">Calls</th>
                <th className="num">Server errors</th>
                <th className="num">Typical</th>
                <th className="num">Slowest 5%</th>
                <th className="num">Slowest 1%</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.method} ${r.route}`}
                  className={r.status === 'bad' ? 'row-bad' : r.status === 'warn' ? 'row-warn' : ''}
                >
                  <td>
                    <span className="meth">{r.method}</span>
                    <span className="route">{r.route}</span>
                  </td>
                  <td className="num">{n0(r.calls)}</td>
                  <td className="num">
                    {r.errors5xx === 0 ? '0' : `${n0(r.errors5xx)} (${pct(r.errorRate)})`}
                  </td>
                  <td className="num">{dur(r.p50Ms)}</td>
                  <td className="num">{dur(r.p95Ms)}</td>
                  <td className="num">{dur(r.p99Ms)}</td>
                  <td className="err-text">
                    {r.lastError ? (
                      <>
                        <b>{r.lastError.message}</b>
                        <div className="prev">{ago(r.lastError.atMs, nowMs)}</div>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <p className="note">
        Percentiles are read from ten counters per minute, so they are the upper edge of a band:
        never better than the truth, and “5 s” means five seconds or more.
      </p>
    </section>
  );
}

function JobHistory({ job, nowMs }: { job: string; nowMs: number }): JSX.Element {
  const q = useQuery({
    queryKey: ['ops', 'job', job],
    queryFn: () =>
      apiFetch<{ runs: OpsJobRun[] }>(`/ops/jobs/${encodeURIComponent(job)}/runs?limit=10`),
  });
  if (q.isLoading) return <p className="muted">Loading…</p>;
  const runs = q.data?.runs ?? [];
  return (
    <ul className="ops-events">
      {runs.map((r) => (
        <li key={r.startedAtMs}>
          <time>{ago(r.startedAtMs, nowMs)}</time>
          <span className={`sev ${r.ok ? 'ok' : 'warn'}`} aria-hidden="true" />
          <span>
            {r.ok ? 'Succeeded' : 'Failed'} in {dur(r.durationMs)}.{r.error ? ` ${r.error}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

function JobsTable({ jobs, nowMs }: { jobs: readonly OpsJobView[]; nowMs: number }): JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  if (jobs.length === 0) {
    return <p className="muted">No scheduled job has run in the last day.</p>;
  }
  return (
    <div className="ops-table-wrap">
      <table className="ops-table">
        <thead>
          <tr>
            <th>Job</th>
            <th>State</th>
            <th className="num">Runs (24 h)</th>
            <th className="num">Failed</th>
            <th className="num">Typical</th>
            <th className="num">Slowest</th>
            <th className="num">Last run</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <JobRow
              key={j.job}
              job={j}
              nowMs={nowMs}
              open={open === j.job}
              onToggle={() => setOpen(open === j.job ? null : j.job)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobRow({
  job: j,
  nowMs,
  open,
  onToggle,
}: {
  job: OpsJobView;
  nowMs: number;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <>
      <tr className={j.status === 'bad' ? 'row-bad' : j.status === 'warn' ? 'row-warn' : ''}>
        <td>
          <button type="button" className="ops-link" aria-expanded={open} onClick={onToggle}>
            <span className="route">{j.job}</span>
          </button>
          {j.reasons.length > 0 ? <div className="prev">{j.reasons[0]}</div> : null}
        </td>
        <td>
          <Chip status={j.status} />
        </td>
        <td className="num">{n0(j.runs)}</td>
        <td className="num">{n0(j.failures)}</td>
        <td className="num">{dur(j.avgMs)}</td>
        <td className="num">{dur(j.maxMs)}</td>
        <td className="num">{j.lastStartedAtMs === null ? '—' : ago(j.lastStartedAtMs, nowMs)}</td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={7}>
            <JobHistory job={j.job} nowMs={nowMs} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Head({
  title,
  sub,
  status,
  reasons,
}: {
  title: string;
  sub: string;
  status: OpsStatus;
  reasons: readonly string[];
}): JSX.Element {
  return (
    <>
      <div className="ops-dhead">
        <h2>
          {title} <Chip status={status} />
        </h2>
        <span className="muted">{sub}</span>
      </div>
      <Why reasons={reasons} />
    </>
  );
}

function ProvidersPanel({ p }: { p: OpsProviders }): JSX.Element {
  const calls = p.items.reduce((n, i) => n + i.calls, 0);
  const refused = p.items.reduce((n, i) => n + i.rejected, 0);
  const unknown = p.items.reduce((n, i) => n + i.unknown, 0);
  return (
    <>
      <Head
        title="SMS, WhatsApp and UPI providers"
        sub={`Calls the API and the worker make to them, the last ${p.windowMinutes} minutes`}
        status={p.status}
        reasons={p.reasons}
      />
      <Tiles
        items={[
          { k: 'Calls', v: n0(calls) },
          { k: 'Refused', v: n0(refused), st: refused > 0 ? 'warn' : 'ok' },
          { k: 'Outcome unknown', v: n0(unknown), st: unknown > 0 ? 'warn' : 'ok' },
          { k: 'From the API', v: perMinute(p.callsPerMinute.api), u: '/min' },
          { k: 'From the worker', v: perMinute(p.callsPerMinute.worker), u: '/min' },
        ]}
      />
      <div className="ops-table-wrap">
        <table className="ops-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>What we asked</th>
              <th>State</th>
              <th className="num">Calls</th>
              <th className="num">Refused</th>
              <th className="num">Outcome unknown</th>
              <th className="num">Typical</th>
              <th className="num">Slowest 5%</th>
            </tr>
          </thead>
          <tbody>
            {p.items.map((i) => (
              <tr
                key={`${i.provider}|${i.operation}`}
                className={i.status === 'bad' ? 'row-bad' : i.status === 'warn' ? 'row-warn' : ''}
              >
                <td>{PROVIDER_LABEL[i.provider] ?? i.provider}</td>
                <td>{OPERATION_LABEL[i.operation] ?? i.operation}</td>
                <td>
                  <Chip status={i.status} />
                  {i.reasons.length > 0 ? <div className="prev">{i.reasons[0]}</div> : null}
                </td>
                <td className="num">{n0(i.calls)}</td>
                <td className="num">{n0(i.rejected)}</td>
                <td className="num">{n0(i.unknown)}</td>
                <td className="num">{i.calls === 0 ? '—' : dur(i.avgMs)}</td>
                <td className="num">{i.calls === 0 ? '—' : dur(i.p95Ms)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="note">
        This counts our side of each call: whether the provider took it, refused it, or left us not
        knowing. An unknown outcome is a timeout or an answer we could not read; the message may or
        may not have gone, and nothing is sent again automatically. Whether a message reached a
        phone is in the provider&apos;s own dashboard.
      </p>
    </>
  );
}

function Pips({
  target,
  label,
}: {
  target: OpsEdge['targets'][number];
  label: string;
}): JSX.Element {
  const ordered = [...target.recent].reverse();
  return (
    <span className="ops-pips" role="img" aria-label={label}>
      {ordered.map((r) => (
        <i key={r.atMs} className={r.ok ? 'ok' : 'bad'} />
      ))}
    </span>
  );
}

function EdgePanel({ edge, nowMs }: { edge: OpsEdge; nowMs: number }): JSX.Element {
  return (
    <>
      <Head
        title="Edge and menu pages"
        sub="Fetched from the worker's machine every minute"
        status={edge.status}
        reasons={edge.reasons}
      />
      <div className="ops-table-wrap">
        <table className="ops-table">
          <thead>
            <tr>
              <th>Check</th>
              <th>State</th>
              <th className="num">Answered</th>
              <th className="num">Last result</th>
              <th>Recent checks, oldest first</th>
            </tr>
          </thead>
          <tbody>
            {edge.targets.map((t) => {
              const passed = t.recent.filter((r) => r.ok).length;
              return (
                <tr
                  key={t.target}
                  className={t.status === 'bad' ? 'row-bad' : t.status === 'warn' ? 'row-warn' : ''}
                >
                  <td className="mono">{t.target}</td>
                  <td>
                    <Chip status={t.status} />
                    {t.reasons.length > 0 ? <div className="prev">{t.reasons[0]}</div> : null}
                  </td>
                  <td className="num">{t.availability === null ? '—' : pct(t.availability)}</td>
                  <td className="num">
                    {t.lastAtMs === null
                      ? '—'
                      : `${t.lastStatus ?? 'no answer'} in ${dur(t.lastMs ?? 0)}, ${ago(t.lastAtMs, nowMs)}`}
                  </td>
                  <td>
                    <Pips
                      target={t}
                      label={`${passed} of the last ${t.recent.length} checks passed`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="note">
        This is a check from the worker&apos;s own machine to the public address: it sees DNS, the
        certificate, Cloudflare and the origin answering. It does not see what a customer&apos;s
        phone sees, and it goes quiet if the whole hosting provider is down. A check you remove from
        the setting stays here for a day, then ages out.
      </p>
    </>
  );
}

const callsOf = (s: readonly OpsSeriesPoint[]): number[] => s.map((p) => p.calls);

export function OpsDetail({ node, ov }: { node: NodeId; ov: OpsOverview }): JSX.Element | null {
  const now = ov.generatedAtMs;
  if (node === 'api') {
    const s = ov.services.api;
    const t = s.traffic;
    return (
      <>
        <Head
          title="API"
          sub={`The last ${ov.windowMinutes} minutes, from every API machine added together`}
          status={s.status}
          reasons={s.reasons}
        />
        <Tiles
          items={[
            { k: 'Calls', v: perMinute(t.callsPerMinute), u: '/min' },
            {
              k: 'Server errors',
              v: pct(t.errorRate),
              u: `of ${n0(t.calls)} calls`,
              st:
                s.status === 'bad' && t.errorRate > 0
                  ? 'bad'
                  : t.errorRate >= 0.015
                    ? 'warn'
                    : 'ok',
            },
            { k: 'Typical', v: dur(t.p50Ms) },
            { k: 'Slowest 5%', v: dur(t.p95Ms) },
            { k: 'Slowest 1%', v: dur(t.p99Ms) },
          ]}
        />
        <section>
          <h3 className="sec">Calls per minute, last hour</h3>
          <Spark values={callsOf(s.series)} label="API calls per minute over the last hour" />
        </section>
        <section>
          <h3 className="sec">Machines</h3>
          <Machines machines={s.machines} nowMs={now} />
        </section>
        <Restarts service="api" nowMs={now} />
        <RouteTable nowMs={now} />
      </>
    );
  }
  if (node === 'worker') {
    const s = ov.services.worker;
    return (
      <>
        <Head
          title="Worker"
          sub="Runs the outbox relay and every scheduled job"
          status={s.status}
          reasons={s.reasons}
        />
        <section>
          <h3 className="sec">Machines</h3>
          <Machines machines={s.machines} nowMs={now} />
        </section>
        <Restarts service="worker" nowMs={now} />
      </>
    );
  }
  if (node === 'relay') {
    const r = ov.relay;
    return (
      <>
        <Head
          title="Outbox relay"
          sub="Moves what the API saved into the work the worker does"
          status={r.status}
          reasons={r.reasons}
        />
        <Tiles
          items={[
            { k: 'Waiting', v: r.sampled ? n0(r.depth) : '—' },
            { k: 'Oldest waiting', v: r.sampled ? dur(r.oldestMs) : '—', st: r.status },
            { k: 'Batches', v: n0(r.batches), u: `in ${ov.windowMinutes} min` },
            { k: 'Delivered', v: n0(r.delivered) },
            { k: 'Failed', v: n0(r.failed), st: r.failed > 0 ? 'warn' : 'ok' },
            { k: 'Rolled back', v: n0(r.rolledBack), st: r.rolledBack > 0 ? 'warn' : 'ok' },
          ]}
        />
        <section>
          <h3 className="sec">Delivered per minute, last hour</h3>
          <Spark
            values={r.series.map((p) => p.delivered)}
            label="Items delivered per minute over the last hour"
          />
        </section>
        {!r.sampled ? (
          <p className="note">
            No queue sample in the last two minutes. If a worker is running this means it has
            stopped sampling; check the worker box.
          </p>
        ) : null}
      </>
    );
  }
  if (node === 'jobs') {
    return (
      <>
        <Head
          title="Scheduled jobs"
          sub="The last 24 hours. Click a job for its recent runs."
          status={ov.jobs.status}
          reasons={[]}
        />
        <JobsTable jobs={ov.jobs.items} nowMs={now} />
      </>
    );
  }
  if (node === 'providers') return <ProvidersPanel p={ov.providers} />;
  if (node === 'edge') return <EdgePanel edge={ov.edge} nowMs={now} />;
  if (node === 'database') {
    const d = ov.database;
    return (
      <>
        <Head
          title="Postgres"
          sub="Checked now, from the API process"
          status={d.status}
          reasons={d.reasons}
        />
        <Tiles
          items={[
            { k: 'Trivial query', v: d.ok ? dur(d.pingMs) : 'no answer', st: d.status },
            { k: 'Connections open', v: n0(d.pool.total) },
            { k: 'Idle', v: n0(d.pool.idle) },
            { k: 'Waiting for one', v: n0(d.pool.waiting), st: d.pool.waiting > 0 ? 'warn' : 'ok' },
          ]}
        />
        <p className="note">
          This is the API&apos;s own view of the database, taken when you opened the page. Slow
          queries and disk are not measured here; Supabase&apos;s console has those.
        </p>
      </>
    );
  }
  return null;
}
